import { defineStore } from "pinia";
import { type ProductData, mergeCurrentData, mergeAttributes } from "@/utils/product/mergeUtils";
import pkg from 'lodash';
const { cloneDeep } = pkg;

interface RevisionData extends ProductData {
  revision_id: number | null;
}

export const useManagementProductAllStore = defineStore(
  "managementProductAllStore",
  () => {
    const isRevisionView = ref(false);

    const originalData = ref<ProductData>({
      general: {},
      attributes: [],
      featuredInfos: [],
      images: [],
    });

    const copyOriginalData = ref<ProductData>({
      general: {},
      attributes: [],
      featuredInfos: [],
      images: [],
    });

    const revisionData = ref<RevisionData>({
      general: {},
      attributes: [],
      featuredInfos: [],
      images: [],
      revision_id: null,
    });

    // Watchers
    watch(isRevisionView, (newValue) => {
      if (!newValue) {
        refreshOriginalData();
      }
    });

    // Computed
    const currentData = computed<ProductData>(() => {
      const merged = mergeCurrentData(
        isRevisionView.value,
        originalData.value,
        revisionData.value
      );
      return reactive(merged);
    });

    const isPublished = computed(() => originalData.value.general.isPublished);

    const hasRevision = computed(() =>
      revisionData.value.revision_id ? true : false
    );

    // Methods
    const clearRevisionData = () => {
      isRevisionView.value = false;

      revisionData.value = {
        general: {},
        attributes: [],
        featuredInfos: [],
        images: [],
        revision_id: null,
      };
    };

    const setRevisionData = (data: Partial<RevisionData>) => {
      if (data.revision_id) {
        revisionData.value = data as RevisionData;
        isRevisionView.value = true;
      }
    };

    const setCopyOriginalData = () => {
      copyOriginalData.value = cloneDeep(originalData.value);
    };

    const setFeaturedInfo = (data: Partial<ProductData>) => {
      if (isPublished.value) {
        revisionData.value.featuredInfos.unshift(data);
        isRevisionView.value = true;
      } else {
        originalData.value.featuredInfos.unshift(data);
      }
    };

    const refreshOriginalData = () => {
      originalData.value = cloneDeep(copyOriginalData.value);
    };

    const setOriginalData = (data: Partial<ProductData>) => {
      originalData.value = {
        ...originalData.value,
        ...data,
      };

      setCopyOriginalData();
    };

    const updateProductWithRevision = (revision: RevisionData) => {
      if (!revision.revision_id) return;

      setOriginalData({
        general: { ...originalData.value.general, ...revision.general },
        images: [...revision.images.length ? revision.images : originalData.value.images],
        attributes: mergeAttributes(originalData.value.attributes, revision.attributes),
        featuredInfos: [...revision.featuredInfos],
      });

      clearRevisionData();
    };

    const removeFeaturedInfo = (id: string | number) => {
      if (isPublished.value) {
        const index = revisionData.value.featuredInfos.findIndex(
          (item) => item.id === id || item.temp_id === id
        );
        if (index !== -1) {
          revisionData.value.featuredInfos.splice(index, 1);
          isRevisionView.value = true;
        }
      } else {
        const index = originalData.value.featuredInfos.findIndex(
          (item) => item.id === id
        );
        if (index !== -1) {
          originalData.value.featuredInfos.splice(index, 1);
        }
      }
    };

    return {
      // State
      isRevisionView,
      originalData,
      revisionData,
      copyOriginalData,

      // Computed
      currentData,
      isPublished,
      hasRevision,

      // Methods
      setFeaturedInfo,
      clearRevisionData,
      setRevisionData,
      setCopyOriginalData,
      refreshOriginalData,
      setOriginalData,
      updateProductWithRevision,
      removeFeaturedInfo,
    };
  }
);
